import { Reveal } from "@/lib/reveal";
import {
  Rocket,
  Palette,
  Target,
  Share2,
  Megaphone,
  Code2,
  ArrowUpRight,
} from "lucide-react";

const SERVICES = [
  {
    icon: Rocket,
    title: "Digital Marketing",
    desc: "Full-funnel strategy, SEO, and analytics built to turn visibility into pipeline.",
    tag: "Growth",
    span: "md:col-span-7",
  },
  {
    icon: Palette,
    title: "Branding",
    desc: "Identity systems that make you look undeniable — logo, voice, visual language.",
    tag: "Identity",
    span: "md:col-span-5",
  },
  {
    icon: Target,
    title: "Lead Generation",
    desc: "Predictable, high-intent pipelines across paid, organic, and outbound.",
    tag: "Pipeline",
    span: "md:col-span-4",
  },
  {
    icon: Share2,
    title: "Social Media Management",
    desc: "Content engines and community ops that compound authority weekly.",
    tag: "Social",
    span: "md:col-span-4",
  },
  {
    icon: Megaphone,
    title: "Paid Ads — Meta & Google",
    desc: "Creative-led ad systems optimised for ROAS, not impressions.",
    tag: "Ads",
    span: "md:col-span-4",
  },
  {
    icon: Code2,
    title: "Website Development",
    desc: "Conversion-engineered websites and landing pages — fast, clean, modern.",
    tag: "Build",
    span: "md:col-span-12",
  },
];

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 border-t border-white/5 bg-gradient-to-b from-black to-[#060606]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <Reveal>
              <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#FF7B00]">
                — What we do
              </span>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="font-display mt-4 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl">
                Services engineered around{" "}
                <span className="text-gradient-gold">revenue outcomes.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={200}>
            <p className="max-w-sm text-zinc-400 text-base md:text-lg leading-relaxed">
              One studio. Six disciplines. Wired together as a single growth
              system for your brand.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {SERVICES.map((s, idx) => (
            <Reveal
              key={s.title}
              delay={idx * 70}
              className={`service-card group rounded-2xl border border-white/5 bg-[#0F0F0F] p-7 md:p-8 hover:-translate-y-1 hover:border-[#FF7B00]/30 transition-all duration-500 ${s.span}`}
            >
              <div
                data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="flex h-full flex-col"
              >
                <div className="flex items-start justify-between">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#FF7B00]/10 border border-[#FF7B00]/20 text-[#FF7B00]">
                    <s.icon className="h-6 w-6" strokeWidth={1.5} />
                  </div>
                  <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-zinc-500">
                    {s.tag}
                  </span>
                </div>
                <div className="mt-8">
                  <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-zinc-400 text-base leading-relaxed max-w-lg">
                    {s.desc}
                  </p>
                </div>
                <div className="mt-auto pt-8 flex items-center gap-2 text-sm font-semibold text-[#FF7B00] opacity-0 group-hover:opacity-100 transition-opacity">
                  Learn more
                  <ArrowUpRight
                    className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    strokeWidth={2.25}
                  />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
